import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { EnhancedButton } from '../../../components/ui/EnhancedButton.jsx';
import ProgressiveFormStep, { 
  ProgressiveFormNavigation, 
  ProgressiveFormProgress 
} from '../../../components/ui/ProgressiveFormStep.jsx';
import { cn } from '../../../utils/cn';
import { CITIES } from '../../../constants/marketData';

const EnhancedROICalculatorForm = ({ onCalculate, isCalculating }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Basic Information
    currentHeadcount: '',
    desiredHeadcount: '',
    
    // Step 2: Financial Details
    averageSalary: '',
    operationalCosts: '',
    
    // Step 3: Project Specifics
    serviceType: '',
    location: '',
    timeline: '',
    
    // Step 4: Additional Context (Optional)
    companySize: '',
    industry: '',
    currentChallenges: []
  });

  const [errors, setErrors] = useState({});
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // Form options
  const serviceTypes = [
    { value: 'software-development', label: 'Software Development', description: 'Custom software, web apps, mobile apps' },
    { value: 'data-analytics', label: 'Data Analytics & AI', description: 'Data science, machine learning, BI' },
    { value: 'customer-support', label: 'Customer Support', description: '24/7 support, help desk, chat support' },
    { value: 'finance-accounting', label: 'Finance & Accounting', description: 'Bookkeeping, financial analysis, audit' },
    { value: 'hr-operations', label: 'HR Operations', description: 'Recruitment, payroll, employee management' },
    { value: 'research-development', label: 'Research & Development', description: 'Innovation, product development, testing' },
    { value: 'digital-marketing', label: 'Digital Marketing', description: 'SEO, content marketing, social media' },
    { value: 'quality-assurance', label: 'Quality Assurance', description: 'Testing, QA automation, compliance' }
  ];

  // Location list driven by marketData.js (12 cities, sourced from NASSCOM-Zinnov / Mercer / state IT policies)
  // costIndex is relative to Bangalore = 100; we render cost as a delta vs Bangalore baseline
  const locations = (typeof CITIES !== 'undefined' ? CITIES : []).map((c) => {
    const delta = c.costIndex - 100;
    const cost = delta === 0 ? 'Baseline' : (delta > 0 ? `+${delta}%` : `${delta}%`);
    return {
      value: c.slug,
      label: c.name,
      description: `${c.primaryVerticals.slice(0, 2).join(' / ')} · ${c.tier === 1 ? 'Tier 1' : 'Tier 2 emerging'}`,
      cost
    };
  });

  const timelines = [
    { value: '6-months', label: '6 Months', description: 'Rapid deployment with premium support', urgency: 'High' },
    { value: '12-months', label: '12 Months', description: 'Standard timeline with optimal costs', urgency: 'Medium' },
    { value: '18-months', label: '18 Months', description: 'Comprehensive setup with phased approach', urgency: 'Low' },
    { value: '24-months', label: '24+ Months', description: 'Strategic long-term implementation', urgency: 'Strategic' }
  ];

  const companySizes = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'small', label: 'Small Business (51-200 employees)' },
    { value: 'medium', label: 'Medium Enterprise (201-1000 employees)' },
    { value: 'large', label: 'Large Enterprise (1000+ employees)' }
  ];

  const industries = [
    { value: 'technology', label: 'Technology & Software' },
    { value: 'financial', label: 'Financial Services' },
    { value: 'healthcare', label: 'Healthcare & Life Sciences' },
    { value: 'manufacturing', label: 'Manufacturing & Industrial' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'consulting', label: 'Consulting & Professional Services' },
    { value: 'education', label: 'Education & Training' },
    { value: 'other', label: 'Other' }
  ];

  const challengeOptions = [
    { value: 'cost-reduction', label: 'Cost Reduction', description: 'Need to reduce operational costs' },
    { value: 'talent-shortage', label: 'Talent Shortage', description: 'Difficulty finding skilled professionals' },
    { value: 'scalability', label: 'Scalability Issues', description: 'Need to scale operations quickly' },
    { value: 'time-zone', label: 'Global Coverage', description: 'Need 24/7 operations or global presence' },
    { value: 'innovation', label: 'Innovation Gap', description: 'Need access to cutting-edge technology' },
    { value: 'market-access', label: 'Market Access', description: 'Want to access Indian market' }
  ];

  const totalSteps = 4;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleChallengeToggle = (challengeValue) => {
    setFormData(prev => ({
      ...prev,
      currentChallenges: prev.currentChallenges.includes(challengeValue)
        ? prev.currentChallenges.filter(c => c !== challengeValue)
        : [...prev.currentChallenges, challengeValue]
    }));
  };

  const validateCurrentStep = () => {
    const newErrors = {};
    
    switch (currentStep) {
      case 1:
        if (!formData.currentHeadcount || formData.currentHeadcount <= 0) {
          newErrors.currentHeadcount = 'Current headcount is required';
        }
        if (!formData.desiredHeadcount || formData.desiredHeadcount <= 0) {
          newErrors.desiredHeadcount = 'Desired headcount is required';
        }
        if (formData.currentHeadcount && formData.desiredHeadcount && 
            parseInt(formData.desiredHeadcount) <= parseInt(formData.currentHeadcount)) {
          newErrors.desiredHeadcount = 'Desired headcount should be greater than current headcount';
        }
        break;
        
      case 2:
        if (!formData.averageSalary || formData.averageSalary <= 0) {
          newErrors.averageSalary = 'Average salary is required';
        }
        if (!formData.operationalCosts || formData.operationalCosts < 0) {
          newErrors.operationalCosts = 'Operational costs are required';
        }
        break;
        
      case 3:
        if (!formData.serviceType) {
          newErrors.serviceType = 'Service type is required';
        }
        if (!formData.location) {
          newErrors.location = 'Location is required';
        }
        if (!formData.timeline) {
          newErrors.timeline = 'Timeline is required';
        }
        break;
        
      case 4:
        // Optional step - no validation required
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep === totalSteps) {
        // Final step - calculate ROI
        onCalculate(formData);
      } else {
        // Mark current step as completed
        setCompletedSteps(prev => new Set([...prev, currentStep]));
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleStepClick = (stepNumber) => {
    // Allow navigation to previous steps or current step
    if (stepNumber <= Math.max(currentStep, Math.max(...completedSteps) + 1)) {
      setCurrentStep(stepNumber);
    }
  };

  const isStepCompleted = (stepNumber) => completedSteps.has(stepNumber);
  const canNavigateToStep = (stepNumber) => stepNumber <= Math.max(currentStep, Math.max(...completedSteps, 0) + 1);

  // Service-specific savings rates — anchored to Zinnov GCC Cost Index 2024 / Everest Group public benchmarks.
  // The 50–60% Zinnov "typical" range reflects fully-loaded India vs US cost ratios; we vary by function.
  const serviceRates = {
    'software-development': 0.55,    // mid-range; senior tech premium is partly absorbed
    'data-analytics': 0.52,          // specialized skills; smaller savings band
    'customer-support': 0.65,        // higher arbitrage but conservative vs older 70%+ claims
    'finance-accounting': 0.58,      // standardized roles; near typical band
    'hr-operations': 0.60,           // standardized roles
    'research-development': 0.48,    // premium talent; lower delta
    'digital-marketing': 0.58,       // mixed talent / tooling costs
    'quality-assurance': 0.55        // mid-range
  };

  // Location cost multipliers derived from CITIES.costIndex (Bangalore = 100 baseline → 1.00x).
  // CostIndex source: Mercer / Numbeo regional indices, Zinnov GCC Cost Index 2024, state IT policy briefs.
  const locationMultipliers = (CITIES || []).reduce((acc, c) => {
    acc[c.slug] = c.costIndex / 100;
    return acc;
  }, {});

  // Timeline urgency premiums (faster = higher cost)
  const timelinePremiums = {
    '6-months': 1.12,     // Rush premium
    '12-months': 1.00,    // Standard
    '18-months': 0.95,    // Some discounts
    '24-months': 0.90     // Best rates
  };

  // Calculate estimated savings with all factors
  const calculateEstimatedSavings = () => {
    if (formData.currentHeadcount && formData.averageSalary) {
      const currentCosts = parseInt(formData.currentHeadcount) * parseInt(formData.averageSalary);
      
      // Get applicable rates
      const baseSavingsRate = serviceRates[formData.serviceType] || 0.55;
      const locationCost = locationMultipliers[formData.location] || 1.0;
      const timelinePremium = timelinePremiums[formData.timeline] || 1.0;
      
      // Adjusted savings = base rate adjusted for location and timeline
      // Higher location cost reduces savings, faster timeline reduces savings
      const adjustedRate = baseSavingsRate / locationCost / timelinePremium;
      const potentialSavings = currentCosts * Math.min(adjustedRate, 0.75); // Cap at 75%
      
      return {
        savings: Math.round(potentialSavings),
        rate: Math.round(Math.min(adjustedRate, 0.75) * 100)
      };
    }
    return { savings: 0, rate: 0 };
  };

  const { savings: estimatedSavings, rate: savingsRate } = calculateEstimatedSavings();

  return (
    <div className="bg-card rounded-xl shadow-executive overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-accent p-6 text-primary-foreground">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-card/20 rounded-lg flex items-center justify-center">
            <Icon name="Calculator" size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold">
              Smart GCC ROI Calculator
            </h3>
            <p className="text-text-primary-foreground/80">
              Get accurate cost savings and ROI projections in 4 simple steps
            </p>
          </div>
        </div>
        
        <ProgressiveFormProgress 
          currentStep={currentStep} 
          totalSteps={totalSteps}
          className="mb-0"
        />
      </div>

      <div className="p-6">
        {/* Real-time Savings Preview */}
        {estimatedSavings > 0 && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <Icon name="TrendingUp" size={20} className="text-success" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-success">Estimated Annual Savings</h4>
                  {savingsRate > 0 && (
                    <span className="text-xs bg-success/20 text-success px-2 py-1 rounded-full">
                      {savingsRate}% cost reduction
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold text-success">
                  ${estimatedSavings.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">
                  {formData.serviceType && formData.location 
                    ? `Based on ${formData.serviceType.replace('-', ' ')} in ${formData.location}`
                    : 'Based on current inputs'} • Complete form for detailed analysis
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-8">
          {/* Step 1: Basic Information */}
          <ProgressiveFormStep
            stepNumber={1}
            totalSteps={totalSteps}
            title="Team Size & Goals"
            description="Tell us about your current team and GCC objectives"
            isActive={currentStep === 1}
            isCompleted={isStepCompleted(1)}
            canNavigate={canNavigateToStep(1)}
            onStepClick={handleStepClick}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Current Team Size"
                type="number"
                placeholder="e.g., 25"
                value={formData.currentHeadcount}
                onChange={(e) => handleInputChange('currentHeadcount', e.target.value)}
                error={errors.currentHeadcount}
                description="Number of employees in current operations"
                icon="Users"
                required
              />

              <Input
                label="Target GCC Team Size"
                type="number"
                placeholder="e.g., 50"
                value={formData.desiredHeadcount}
                onChange={(e) => handleInputChange('desiredHeadcount', e.target.value)}
                error={errors.desiredHeadcount}
                description="Desired team size for your GCC"
                icon="Target"
                required
              />
            </div>

            {formData.currentHeadcount && formData.desiredHeadcount && (
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Info" size={16} className="text-accent" />
                  <span className="text-sm text-muted-foreground">
                    You're planning to scale from {formData.currentHeadcount} to {formData.desiredHeadcount} employees 
                    ({Math.round(((formData.desiredHeadcount - formData.currentHeadcount) / formData.currentHeadcount) * 100)}% growth)
                  </span>
                </div>
              </div>
            )}
          </ProgressiveFormStep>

          {/* Step 2: Financial Details */}
          <ProgressiveFormStep
            stepNumber={2}
            totalSteps={totalSteps}
            title="Financial Information"
            description="Current costs and financial parameters"
            isActive={currentStep === 2}
            isCompleted={isStepCompleted(2)}
            canNavigate={canNavigateToStep(2)}
            onStepClick={handleStepClick}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Average Annual Salary"
                type="number"
                placeholder="e.g., 85000"
                value={formData.averageSalary}
                onChange={(e) => handleInputChange('averageSalary', e.target.value)}
                error={errors.averageSalary}
                description="Current average salary per employee (USD)"
                icon="DollarSign"
                prefix="$"
                required
              />

              <Input
                label="Annual Operational Costs"
                type="number"
                placeholder="e.g., 500000"
                value={formData.operationalCosts}
                onChange={(e) => handleInputChange('operationalCosts', e.target.value)}
                error={errors.operationalCosts}
                description="Infrastructure, tools, and overhead costs (USD)"
                icon="Building"
                prefix="$"
                required
              />
            </div>

            {formData.averageSalary && formData.currentHeadcount && (
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Current Annual Payroll</p>
                    <p className="text-lg font-semibold text-foreground">
                      ${(formData.averageSalary * formData.currentHeadcount).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total Current Costs</p>
                    <p className="text-lg font-semibold text-foreground">
                      ${((formData.averageSalary * formData.currentHeadcount) + parseInt(formData.operationalCosts || 0)).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Est. GCC Savings</p>
                    <p className="text-lg font-semibold text-success">
                      ${estimatedSavings.toLocaleString()}
                    </p>
                    {savingsRate > 0 && (
                      <p className="text-xs text-success">({savingsRate}% reduction)</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </ProgressiveFormStep>

          {/* Step 3: Project Specifics */}
          <ProgressiveFormStep
            stepNumber={3}
            totalSteps={totalSteps}
            title="Project Requirements"
            description="Service type, location, and timeline preferences"
            isActive={currentStep === 3}
            isCompleted={isStepCompleted(3)}
            canNavigate={canNavigateToStep(3)}
            onStepClick={handleStepClick}
          >
            <div className="space-y-6">
              <Select
                label="Primary Service Type"
                placeholder="Select the main service for your GCC"
                options={serviceTypes}
                value={formData.serviceType}
                onChange={(value) => handleInputChange('serviceType', value)}
                error={errors.serviceType}
                searchable
                required
                renderOption={(option) => (
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-sm text-muted-foreground">{option.description}</div>
                  </div>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select
                  label="Preferred Location"
                  placeholder="Select GCC location"
                  options={locations}
                  value={formData.location}
                  onChange={(value) => handleInputChange('location', value)}
                  error={errors.location}
                  searchable
                  required
                  renderOption={(option) => (
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{option.label}</div>
                        <div className="text-sm text-muted-foreground">{option.description}</div>
                      </div>
                      <span className={cn(
                        "text-xs px-2 py-1 rounded-full",
                        option.cost.includes('+') ? "bg-warning text-warning-foreground" :
                        option.cost.includes('-') ? "bg-success text-success-foreground" :
                        "bg-muted text-text-primary"
                      )}>
                        {option.cost}
                      </span>
                    </div>
                  )}
                />

                <Select
                  label="Implementation Timeline"
                  placeholder="Select timeline"
                  options={timelines}
                  value={formData.timeline}
                  onChange={(value) => handleInputChange('timeline', value)}
                  error={errors.timeline}
                  required
                  renderOption={(option) => (
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{option.label}</div>
                        <div className="text-sm text-muted-foreground">{option.description}</div>
                      </div>
                      <span className={cn(
                        "text-xs px-2 py-1 rounded-full",
                        option.urgency === 'High' ? "bg-red-100 text-red-700" :
                        option.urgency === 'Medium' ? "bg-yellow-100 text-yellow-700" :
                        option.urgency === 'Low' ? "bg-blue-100 text-blue-700" :
                        "bg-purple-100 text-purple-700"
                      )}>
                        {option.urgency}
                      </span>
                    </div>
                  )}
                />
              </div>
            </div>
          </ProgressiveFormStep>

          {/* Step 4: Additional Context (Optional) */}
          <ProgressiveFormStep
            stepNumber={4}
            totalSteps={totalSteps}
            title="Additional Context"
            description="Optional details for more accurate calculations (can be skipped)"
            isActive={currentStep === 4}
            isCompleted={isStepCompleted(4)}
            canNavigate={canNavigateToStep(4)}
            onStepClick={handleStepClick}
          >
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select
                  label="Company Size"
                  placeholder="Select company size (optional)"
                  options={companySizes}
                  value={formData.companySize}
                  onChange={(value) => handleInputChange('companySize', value)}
                />

                <Select
                  label="Industry"
                  placeholder="Select industry (optional)"
                  options={industries}
                  value={formData.industry}
                  onChange={(value) => handleInputChange('industry', value)}
                  searchable
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  Current Business Challenges (select all that apply)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {challengeOptions.map((challenge) => (
                    <button
                      key={challenge.value}
                      type="button"
                      onClick={() => handleChallengeToggle(challenge.value)}
                      className={cn(
                        "text-left p-4 rounded-lg border transition-all duration-200",
                        "hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20",
                        formData.currentChallenges.includes(challenge.value)
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border hover:bg-muted/50"
                      )}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={cn(
                          "w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5",
                          formData.currentChallenges.includes(challenge.value)
                            ? "border-primary bg-primary"
                            : "border-border"
                        )}>
                          {formData.currentChallenges.includes(challenge.value) && (
                            <Icon name="Check" size={12} className="text-primary-foreground" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-sm">{challenge.label}</div>
                          <div className="text-xs text-muted-foreground">{challenge.description}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="Lightbulb" size={20} className="text-accent mt-0.5" />
                  <div>
                    <h4 className="font-medium text-foreground mb-1">Pro Tip</h4>
                    <p className="text-sm text-muted-foreground">
                      Providing additional context helps us generate more accurate ROI calculations and 
                      personalized recommendations for your specific business needs.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ProgressiveFormStep>
        </div>

        {/* Navigation */}
        <ProgressiveFormNavigation
          currentStep={currentStep}
          totalSteps={totalSteps}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onSkip={handleSkip}
          showSkip={currentStep === 4}
          isNextDisabled={!validateCurrentStep() && currentStep < totalSteps}
          isLoading={isCalculating}
          className="mt-8"
        />
      </div>
    </div>
  );
};

export default EnhancedROICalculatorForm;
